import { dehydrate, QueryClient, useMutation } from "@tanstack/react-query";
import { processQueryArgs } from "../../utils/processQueryArgs";
import { buildContentFilterQueryParams } from "../../pagelayouts/content/utils/buildContentFilterQueryParams";
import toast from "react-hot-toast";
import { APPLICATION_ROUTES } from "../../shared-logic/routes";
import { ProfileUserResponse, ShareStatus } from "@authory-packages/core";
import { useRouter } from "next/router";
import { getContentInfiniteQuerie } from "../../utils/query-utils/contentInfiniteQuerie";
import { ContactUser } from "../../api/user";
import { V3GenericErrorHandler } from "../../api/error-handler";
import { ContactUserFormik } from "../../components/V3ContactUser";
import { ProfileSearchForm, V4ProfilePublicPageLayout } from "../../pagelayouts/profile/V4ProfilePublicPageLayout";
import { subscribe } from "../../api/subscribe";
import { cloneDeep } from "lodash";
import { withGlobalPageSpinner } from "../../components/GlobalPageSpinner";
import { RequestHost } from "../../types/headers";
import { useContext, useEffect, useState } from "react";
import { DomainContext } from "../../context/domainContext";
import { getProfileClient, getProfileServer, getPublicUserServer } from "../../utils/getAuthor";
import { getAuthorContentQuerie } from "../../types/queries";
import { NextPageContext } from "next";
import { getUserContent } from "../../api/content";
import { serverSideErrorResponse, statusResponse, unavailableResponse } from "../../utils/unavailableResource";
import { isHiddenAsSpam } from "../../utils/isHiddenAsSpam";
import { Errors } from "../../types/api";
import { UNAVAILABLE_REASON, UNAVAILABLE_RESOURCE_TYPE } from "../../types/unavailable_resource_type";
import { SmartSessionTracker } from "../../smart-tracking";

interface V4ProfilePublicPageLayoutContainerProps {
    profileShareStatus?: ShareStatus;
    isSmartPage?: boolean;
}

export const V4ProfilePublicPageLayoutContainer = ({ profileShareStatus, isSmartPage = false }: V4ProfilePublicPageLayoutContainerProps) => {
    const router = useRouter();
    const requestHostHeader: RequestHost = useContext(DomainContext) ?? RequestHost.MAINDOMAIN;
    const isMainDomain = requestHostHeader === RequestHost.MAINDOMAIN;
    const aboutPageSlug = router.query.aboutPageSlug as string;
    const smartShareUrl = router.query.smartShareURL as string;
    const [smartSessionTracker, setSmartSessionTracker] = useState<SmartSessionTracker | null>(null);

    // Get client profile
    const { author, userSmartShareLink } = getProfileClient(profileShareStatus, isSmartPage);

    const aboutPage = author.data?.aboutPages.find((abt) => abt.slug === aboutPageSlug);
    const { searchArgs } = processQueryArgs(router.query, author.data);

    // Get the articles
    const params = buildContentFilterQueryParams(searchArgs, true, undefined, true);
    const articles = getContentInfiniteQuerie(null, author.data?.slug, params, params.collection !== undefined);

    useEffect(() => {
        if (author.isSuccess && smartShareUrl && author.data?.slug && userSmartShareLink?.data?.active && smartSessionTracker === null) {
            setSmartSessionTracker(new SmartSessionTracker(userSmartShareLink?.data?.uid, author.data?.slug));
        }
    }, [author.isSuccess]);

    // Contact User Mutation
    const contactUserMutation = useMutation({
        mutationFn: ContactUser,
        onError: V3GenericErrorHandler(toast.error)
    });

    const onContactUserSubmit = async (data: ContactUserFormik) => {
        if (!author.data) return;

        await contactUserMutation.mutateAsync({ userSlug: author.data?.slug, data });
    };

    const subcribeMutation = useMutation({
        mutationFn: subscribe,
        onError: V3GenericErrorHandler(toast.error)
    });

    const onSubscribeFormSubmit = async (email: string) => {
        if (!author.data) return;
        await subcribeMutation.mutateAsync({ email, userId: author.data?.slug });
    };

    const onSearchFormSubmit = (formikData: ProfileSearchForm) => {
        if (!author.data) return;

        const queryParams = [];
        const data = cloneDeep(formikData);

        for (const [key, value] of Object.entries(data)) {
            //@ts-ignore
            if (data[key] && data[key].length) queryParams.push(`${key}=${value}`);
        }

        router.push(
            `${APPLICATION_ROUTES.AUTHOR_PAGE.replace("[author]", isMainDomain ? author.data?.slug : "")}?${queryParams.join("&")}`,
            undefined,
            {
                shallow: true
            }
        );
    };

    const isLoading = author.isLoading;

    return (
        <>
            {withGlobalPageSpinner(
                isLoading,
                <V4ProfilePublicPageLayout
                    author={author.data!}
                    items={articles.data?.pages || []}
                    itemsLoading={articles.isFetching}
                    onContactUserSubmit={onContactUserSubmit}
                    onSubscribeFormSubmit={onSubscribeFormSubmit}
                    contentCanFetchmore={articles.hasNextPage || false}
                    contentLoadmore={articles.fetchNextPage}
                    onSearchFormSubmit={onSearchFormSubmit}
                    queryArgs={searchArgs as Record<string, string>}
                    subscribedModalOpen={router.query.target === "subscribed"}
                    unsubscribedModalOpen={router.query.target === "unsubscribed"}
                    initialContactOpen={router.query.target === "contact"}
                    visitorBannerOpen={router.query.target === "visitor"}
                    subscribeOpen={router.query.target === "subscribe"}
                    rules={[]}
                    key={aboutPageSlug}
                    userAboutPage={aboutPage}
                    isAboutPage={!!aboutPageSlug}
                    smartShareUrl={smartShareUrl}
                    onItemClick={(id) => {
                        smartSessionTracker && smartSessionTracker.trackArticle(id);
                    }}
                />
            )}
        </>
    );
};

export const getServerSidePropsForProfile = (isSmartSharePage: boolean) => async (ctx: NextPageContext) => {
    const queryClient = new QueryClient();

    try {
        const authorSlug = ctx.query.author as string;

        // Get public user first - all public pages must do this
        const publicUser = await getPublicUserServer(queryClient, ctx);
        if (!publicUser) return unavailableResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.PROFILE, UNAVAILABLE_REASON.NOT_FOUND);
        // A plain 404 with nothing dehydrated, so the page html carries none of the account's data
        if (isHiddenAsSpam(publicUser)) return statusResponse(ctx, Errors.NotFound);

        // User main profile page share restrictions
        const isNotShared = publicUser && publicUser.profileShareStatus === ShareStatus.Unpublished;
        if (!isSmartSharePage && isNotShared) {
            return unavailableResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.PROFILE, UNAVAILABLE_REASON.NOT_SHARED);
        }

        const fetchProfileDataOnSSR = publicUser.profileShareStatus === ShareStatus.Published || isSmartSharePage;

        // If the profile is published - fetch profile on SSR
        let author: ProfileUserResponse | undefined = undefined;
        if (fetchProfileDataOnSSR) {
            // Get profile
            const profileServerRes = await getProfileServer(queryClient, ctx, isSmartSharePage);

            author = profileServerRes.author;

            if (profileServerRes.smartShareLink && !profileServerRes.smartShareLink.active) {
                return unavailableResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.PROFILE, UNAVAILABLE_REASON.NOT_SHARED);
            }

            // Get the articles
            const { searchArgs } = processQueryArgs(ctx.query, author);
            const params = buildContentFilterQueryParams(searchArgs, true, undefined, true);
            const articleQuery = getAuthorContentQuerie(null, authorSlug, params);

            await queryClient.prefetchInfiniteQuery({
                queryKey: articleQuery,
                queryFn: () => getUserContent(null, authorSlug, params),
                initialPageParam: 1
            });
        }

        return {
            props: {
                dehydratedState: dehydrate(queryClient),
                statusCode: ctx.res?.statusCode,
                author: author || null,
                profileShareStatus: publicUser?.profileShareStatus
            }
        };
    } catch (error) {
        return serverSideErrorResponse(ctx, queryClient, UNAVAILABLE_RESOURCE_TYPE.PROFILE, error);
    }
};
